import type { DayPayout, PayableBundle } from '@/domain/payoutFlow';
import type { ArtistCover } from '@/domain/cashFlow';

/**
 * The red/green pay flag for one artist row on the payout surface. A pure
 * function of the row-level reads the panel already holds — READ, DON'T
 * RECOMPUTE: the underlying maths lives in `payableBundle` (the drawer bundle)
 * and `dayCoverTimeline` (the forecast). This only picks which of the two
 * verdicts applies and reports the colour.
 *
 * Green ('good'): the artist is payable now and the drawer covers the whole
 * envelope (no blocked days), or — while still in the chair — the day's forecast
 * covers them. An artist with nothing outstanding / already signed is green too.
 *
 * Red ('short'): a genuine shortfall with the float sealed — the drawer cannot
 * cover the whole bundle (payable-now branch), or the forecast falls short by
 * this artist's finish (in-chair branch).
 */
export type PayFlag = 'good' | 'short';

export function artistPayFlag(
  payable: Pick<DayPayout, 'payable'>,
  cover: Pick<ArtistCover, 'covered' | 'signed'>,
  bundle: Pick<PayableBundle, 'blockedDayIsos' | 'shortAud'>,
): PayFlag {
  // Already paid off: nothing to flag red.
  if (cover.signed) return 'good';

  // Payable now: the drawer decides. Covers the whole envelope (nothing blocked)
  // → good; a blocked day is a genuine shortfall with the float sealed → short.
  if (payable.payable) {
    return bundle.blockedDayIsos.length === 0 ? 'good' : 'short';
  }

  // Still in the chair: the day's forecast decides.
  return cover.covered ? 'good' : 'short';
}
